import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { changeText, dayLabel, fmtDate } from "./adjustUtils";

const OPTIONS = [["", "Keep as is"], ["present", "Present"], ["half_day", "Half day"], ["absent", "Absent"]];

function PendingNote({ pending, onCancel }) {
  const { user } = useAuth();
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center justify-between gap-2" data-testid="adj-pending-note">
      <span>Awaiting approval: <b>{changeText(pending)}</b>. Saving again replaces it.</span>
      {pending.requested_by === user?.id && (
        <Button size="sm" variant="ghost" className="h-7" onClick={onCancel} data-testid="adj-cancel-pending">Withdraw</Button>
      )}
    </div>
  );
}

export function AdjustmentDialog({ pick, locked, onClose, onSaved }) {
  const [f, setF] = useState({ status: "", ot: "", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ status: "", ot: "", reason: "" }); }, [pick]);
  if (!pick) return null;
  const { emp, day, cell } = pick;

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/attendance-adjustments", {
        employee_id: emp.id, date: day.date, status: f.status || null,
        ot_hours: f.ot === "" ? null : Number(f.ot), reason: f.reason,
      });
      toast.success("Sent to another admin for approval");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.post(`/attendance-adjustments/${cell.pending.id}/cancel`); toast.success("Withdrawn"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="adj-dialog">
        <DialogHeader><DialogTitle>{emp.name} · {fmtDate(day.date)}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-md border border-zinc-200 px-3 py-2"><div className="text-zinc-500">Device / app</div>
              <div className="font-medium mt-0.5" data-testid="adj-device-value">{cell ? dayLabel(cell.dv) : "No punch"}{cell?.dot > 0 ? ` · OT ${cell.dot}h` : ""}</div></div>
            <div className="rounded-md border border-zinc-200 px-3 py-2"><div className="text-zinc-500">Used for payroll now</div>
              <div className="font-medium mt-0.5" data-testid="adj-current-value">{cell ? dayLabel(cell.v) : "No punch"}{cell?.ot > 0 ? ` · OT ${cell.ot}h` : ""}{cell?.adj ? " (adjusted)" : ""}</div></div>
          </div>
          {cell?.pending && <PendingNote pending={cell.pending} onCancel={cancel}/>}
          {emp.monthly_sheet && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="adj-monthly-sheet-note">A monthly attendance sheet with fixed totals is uploaded for this employee. Payroll uses the sheet's totals, so day changes here won't change pay unless the sheet is removed on the Payroll Run page.</div>}
          {locked && <div className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="adj-arrears-note">This month's payroll is finalised. Once approved, the difference is paid (or recovered) as arrears in the next payroll.</div>}
          <div>
            <Label>Mark the day as</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {OPTIONS.map(([k, l]) => (
                <button key={k || "keep"} type="button" onClick={() => setF({ ...f, status: k })} data-testid={`adj-status-${k || "keep"}`}
                  className={`px-3 h-8 rounded-full border text-xs transition-colors ${f.status === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-50"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Overtime hours</Label>
              <Input type="number" min="0" max="16" step="0.5" placeholder="Keep as is" className="mt-1" value={f.ot}
                onChange={e => setF({ ...f, ot: e.target.value })} data-testid="adj-ot-input"/></div>
          </div>
          <div><Label>Reason</Label>
            <Textarea className="mt-1" placeholder="e.g. Worked at client site, forgot to punch" value={f.reason}
              onChange={e => setF({ ...f, reason: e.target.value })} data-testid="adj-reason-input"/></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy || f.reason.trim().length < 3 || (!f.status && f.ot === "")} data-testid="adj-submit">
            {busy ? "Sending…" : "Send for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
